import { type LoginModeResponse, Session } from '@asset-metrix/portal-auth';
import { RoleDto } from '@asset-metrix/portal-gateway-client-js';
import { AsyncNotification } from '@asset-metrix/portal-ui';
import type { StateCreator } from 'zustand';

import { PORTAL_API_URL } from '../../config';
import { PermanentError } from '../../utils/errors';
import { queryClient } from '../../utils/queryClient';
import type { RemoveFunctions } from '../../utils/types';
import type { StateSlices } from '..';

type LogoutSessionOptions = {
  initiatedByUser?: boolean;
  persistCurrentPage?: boolean;
};

export type SessionSlice = {
  activeUserRole?: RoleDto;
  apiId?: string;
  initialLoginModeData?: LoginModeResponse;
  session?: Session;
  sessionId: string;
  tenantName?: string;
  getDisclaimerVersion(): number | null;
  initSession(apiId: string): Promise<void>;
  isSessionAuthenticated(): boolean;
  isSessionExpired(): boolean;
  loginSession(email: string, password: string): Promise<void>;
  /**
   * @param initiatedByUser If set to 'true' the logout will also reset UI settings stored in the browser
   */
  logoutSession(options?: LogoutSessionOptions): Promise<void>;
  refreshSession(logoutOnFail?: boolean): Promise<void>;
  resetSessionSlice: () => void;
  setActiveUserRole(role: RoleDto): void;
  setApiId(apiId: string): void;
  setTenantName(apiId: string): void;
};

const NO_SESSION_ID = 'no-session-id';

const initialState: RemoveFunctions<SessionSlice> = {
  sessionId: NO_SESSION_ID,
  activeUserRole: undefined,
};

const isRelevantRedirectUrl = (pathname: string) =>
  pathname.includes('/p/') || !pathname.endsWith('/login');

export const createSessionSlice: StateCreator<
  StateSlices,
  [],
  [],
  SessionSlice
> = (set, get) => ({
  ...initialState,
  getDisclaimerVersion() {
    return (
      get().session?.getDecodedAuthToken()?.identity.disclaimer_version ?? null
    );
  },
  async initSession(apiId) {
    const session = new Session({
      authTokenKey: ['auth_token', /* For SSO */ 'token'],
      portalApi: {
        apiId,
        baseUrl: new URL(PORTAL_API_URL).origin,
      },
    });

    // Re-set auth token on session init
    const token = session.getAuthToken();
    if (token) {
      session.setAuthToken(token);
    }

    if (session.isAuthTokenExpired()) {
      if (session.getRefreshToken() != null) {
        try {
          await session.refreshAuthToken();
        } catch {
          // Access token and refresh token are both expired. To avoid unecessary retries we remove them from local storage
          session.clear('EXCEPT_REDIRECT_URL');
        }
      } else {
        // Access token is expired, but no refresh token is available. User needs to log in again.
        session.clear('EXCEPT_REDIRECT_URL');
      }
    }

    // Follow a stored return URL only when the session is authenticated and does
    // not still have to change its local password. When logged out, store the
    // current path if it is a useful return target. SSO applies its return URL
    // through the identity provider; local login applies the stored one after
    // sign-in.
    const decodedToken = session.getDecodedAuthToken();
    if (decodedToken != null && !session.hasToChangePassword()) {
      const redirectUrl = session.getRedirectUrl({ clear: true });
      if (redirectUrl) {
        // Need to use basic browser functionality, because redirect url includes e.g. the router basename
        globalThis.location.assign(redirectUrl);
      }
    } else if (
      decodedToken == null &&
      isRelevantRedirectUrl(globalThis.location.pathname)
    ) {
      session.setRedirectUrl(
        `${globalThis.location.pathname}${globalThis.location.search}`
      );
    }

    // We need to retrieve the initial login mode in any case, because portal-api does only accept more specific login
    // mode requests (with the email address) if the initial login mode is 'multi'
    const initialLoginModeData = await session.getLoginMode();

    set({
      ...(decodedToken?.identity?.disclaimer_version != null
        ? { disclaimerVersion: decodedToken.identity.disclaimer_version }
        : {}),
      initialLoginModeData,
      session,
      sessionId: decodedToken?.jti ?? NO_SESSION_ID,
    });
  },
  isSessionAuthenticated() {
    return get().session?.getDecodedAuthToken() != null;
  },
  isSessionExpired() {
    return get().session?.isAuthTokenExpired() ?? false;
  },
  async loginSession(email, password) {
    const session = get().session;

    if (!session) {
      throw new PermanentError('Session not initialized');
    }

    await session.login(email, password);

    set({
      sessionId: session.getDecodedAuthToken()?.jti ?? NO_SESSION_ID,
    });
  },
  async logoutSession({ initiatedByUser, persistCurrentPage } = {}) {
    // clear react-query cache
    queryClient.removeQueries({
      predicate: (query) => {
        // do not clear pre login data as it leads to a recursive loop
        return !query.queryHash.includes('tenantLoginInfo');
      },
    });

    // clear all visible and queued notifications
    AsyncNotification.clean();

    // set current page as redirect url
    if (
      persistCurrentPage &&
      get().session?.getRedirectUrl() == null &&
      isRelevantRedirectUrl(globalThis.location.pathname)
    ) {
      get().session?.setRedirectUrl(
        `${globalThis.location.pathname}${globalThis.location.search}`
      );
    }

    await get().session?.logout();
    get().resetAllSlices(initiatedByUser);
  },
  async refreshSession(logoutOnFail = true) {
    const session = get().session;

    if (!session) {
      throw new PermanentError('Session not initialized');
    }

    try {
      await session.refreshAuthToken();
    } catch (err) {
      console.log('Session refresh failed', err);

      if (logoutOnFail) {
        try {
          await get().logoutSession({ persistCurrentPage: true });
        } catch {
          // Ignore
        }
      } else {
        throw err;
      }
    }

    set({ sessionId: session.getDecodedAuthToken()?.jti ?? NO_SESSION_ID });
  },
  resetSessionSlice() {
    set({ ...initialState });
  },
  setActiveUserRole(activeUserRole) {
    set({ activeUserRole });
  },
  setApiId(apiId) {
    set({ apiId });
  },
  setTenantName(tenantName) {
    set({ tenantName });
  },
});
